import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./ContactSection.css";

const BcaContactSection = () => {
  const formSectionRef = useRef(null);
  const infoSectionRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("show");
          }
        });
      },
      { threshold: 0.2 }
    );

    if (formSectionRef.current) observer.observe(formSectionRef.current);
    if (infoSectionRef.current) observer.observe(infoSectionRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.subject || !formData.message) {
      setErrorMessage("All fields are required ❌");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage("Please enter a valid email ❌");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await axios.post("https://bihanichildrensacademy.com/api/contacts", {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message
      });

      setSuccessMessage(response.data.message);
      
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });

    } catch (error) {
      console.error("Error submitting form:", error);
      setErrorMessage(error.response?.data?.message || "Error sending message. Please try again. ❌");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {successMessage && (
        <div className="bca-notification bca-notification-success">
          <div className="bca-notification-content">
            {successMessage}
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="bca-notification bca-notification-error">
          <div className="bca-notification-content">
            {errorMessage}
          </div>
        </div>
      )}

      <section className="bca-contact-banner">
        <div className="bca-banner-overlay"></div>
        <div className="bca-banner-content">
          <p className="bca-banner-subtitle">GET IN TOUCH</p>
          <h1>CONTACT</h1>
        </div>
      </section>

      <section className="bca-contact-main">
        <div ref={formSectionRef} className="bca-form-section bca-fade-left">
          <h2>Get in Touch</h2>

          <form onSubmit={handleSubmit}>
            <label>Your Name (required)</label>
            <input 
              type="text" 
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Your Name..."
              disabled={loading}
            />

            <label>Your E-mail (required)</label>
            <input 
              type="email" 
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Your E-mail..."
              disabled={loading}
            />

            <label>Phone Number (required)</label>
            <input 
              type="tel" 
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Phone Number..."
              disabled={loading}
            />

            <label>Subject (required)</label>
            <input 
              type="text" 
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              placeholder="Subject..."
              disabled={loading}
            />

            <label>Message (required)</label>
            <textarea 
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Message..."
              disabled={loading}
            />

            <button type="submit" disabled={loading}>
              {loading ? "SENDING..." : "SEND"}
            </button>
          </form>
        </div>

        <div ref={infoSectionRef} className="bca-info-section bca-fade-right">
          <p className="bca-section-subtitle">HAVE MORE QUESTIONS?</p>
          <h1>Contact Us</h1>

          <p className="bca-description">
            Have questions or need assistance? Whether you're interested in
            admissions, learning more about our programs, or connecting with our
            team — we're here to help.
          </p>

          <div className="bca-info-cards">
            <div className="bca-info-card">
              <div className="bca-card-header">
                <svg className="bca-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <h3>By Location :</h3>
              </div>
              <div className="bca-card-body">
                <p>Bihani Children Academy Gaganpath, Jawahar Nagar, Sri Ganganagar, Rajasthan-335001</p>
              </div>
            </div>

            <div className="bca-info-card">
              <div className="bca-card-header">
                <svg className="bca-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <h3>By Phone</h3>
              </div>
              <div className="bca-card-body">
                <p className="bca-card-label">Phone No.</p>
                <p className="bca-card-value">0154-2480607</p>
              </div>
            </div>

            <div className="bca-info-card">
              <div className="bca-card-header">
                <svg className="bca-card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="m2 7 10 7 10-7"/>
                </svg>
                <h3>By Email</h3>
              </div>
              <div className="bca-card-body">
                <p className="bca-card-label">Email :</p>
                <p className="bca-card-value">admin@bihanichildrensacademy.com</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bca-map-section">
        <iframe
          title="Sri Ganganagar Location"
          src="https://www.google.com/maps?q=Bihani+Children+Academy+Gaganpath+Sri+Ganganagar&output=embed"
          loading="lazy"
        ></iframe>
      </section>
    </>
  );
};

export default BcaContactSection;